import { useContext, useEffect, useMemo, useState } from 'react';

import { ApolloProvider } from '@apollo/client';
import { QueryClientProvider } from '@tanstack/react-query';
import 'primeicons/primeicons.css';
import {
  InputStyleType,
  PrimeReactContext,
  PrimeReactProvider,
} from 'primereact/api';

import { ToastContainer } from '@/components';
import { Toaster } from '@/components/ui/toast';
import { TooltipProvider } from '@/components/ui/tooltip';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import AppRouter from '@/routes/routes';
import { client, queryClient } from '@/services';
import {
  initialState,
  PrimaryContext,
} from '@/services/context/PrimaryContext';
import { DonorSchedule, SystemUser } from '@/types.gql';
import { adjustFontSize, removeExtraThemeLinks } from '@/utils/preferences';

import { useAppSelector } from './app/hooks';
import { ToastProvider } from './services/toast';

import './index.css';

type AppProps = {
  children: React.ReactNode;
};

const AppContent = ({ children }: AppProps) => {
  const { changeTheme, setInputStyle, setRipple } =
    useContext(PrimeReactContext);

  const { inputStyle, ripple, scale, theme, previousTheme } = useAppSelector(
    (state) => state.preferences,
  );

  useEffect(() => {
    removeExtraThemeLinks();

    changeTheme!(
      `/css/themes/${previousTheme}/theme.css`,
      `/css/themes/${theme}/theme.css`,
      'theme-link',
    );

    setInputStyle?.(inputStyle as InputStyleType);
    setRipple?.(ripple);
    adjustFontSize(scale);
  }, [
    changeTheme,
    inputStyle,
    previousTheme,
    ripple,
    scale,
    setInputStyle,
    setRipple,
    theme,
  ]);

  return <>{children}</>;
};

const PrimeReactWrapper = ({ children }: AppProps) => {
  const disableAutocomplete = useFeatureFlag('Remove_Html_Autocomplete');

  const primeReactValue = useMemo(
    () =>
      disableAutocomplete
        ? {
            pt: {
              inputtext: { root: { autoComplete: 'off' } },
              inputnumber: { input: { root: { autoComplete: 'off' } } },
            },
          }
        : {},
    [disableAutocomplete],
  );

  return (
    <PrimeReactProvider value={primeReactValue}>{children}</PrimeReactProvider>
  );
};

const App = () => {
  const [donorSchedules, setDonorSchedules] = useState(
    initialState.donorSchedules,
  );

  const [selectedDonorSchedules, setSelectedDonorSchedules] = useState(
    initialState.selectedDonorSchedules,
  );

  const [workingDonorSchedule, setWorkingDonorSchedule] = useState(
    initialState.workingDonorSchedule,
  );

  const [csr, setCsr] = useState<SystemUser>();

  const [quickFilterText, setQuickFilterText] = useState<string | undefined>();

  const [selectedDonor, setSelectedDonor] = useState<
    DonorSchedule | undefined
  >();

  const [partnership, setPartnership] = useState(initialState.partnership);

  return (
    <ApolloProvider client={client}>
      <QueryClientProvider client={queryClient}>
        <PrimeReactWrapper>
          <AppContent>
            <PrimaryContext.Provider
              value={{
                donorSchedules,
                setDonorSchedules,
                selectedDonorSchedules,
                setSelectedDonorSchedules,
                workingDonorSchedule,
                setWorkingDonorSchedule,
                partnership,
                setPartnership,
                csr,
                setCsr,
                quickFilterText,
                setQuickFilterText,
                selectedDonor,
                setSelectedDonor,
              }}
            >
              <ToastContainer />
              <Toaster />

              <ToastProvider>
                <TooltipProvider>
                  <AppRouter />
                </TooltipProvider>
              </ToastProvider>
            </PrimaryContext.Provider>
          </AppContent>
        </PrimeReactWrapper>
      </QueryClientProvider>
    </ApolloProvider>
  );
};

export default App;
